import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';

import { BONUS_QUESTION, SHOWER_QUESTIONS } from './bridal-shower-questions.data';
import { ShowerQuestion } from './bridal-shower-questions.model';
import { Petal, createPetals } from './bridal-shower-petals';

@Component({
  selector: 'app-bridal-shower-questions',
  templateUrl: './bridal-shower-questions.component.html',
  styleUrls: ['./bridal-shower-questions.component.scss'],
  standalone: false,
})
export class BridalShowerQuestionsComponent implements OnInit {
  readonly questions: ShowerQuestion[] = SHOWER_QUESTIONS;
  readonly bonus: ShowerQuestion = BONUS_QUESTION;

  openId: number | null = null;
  sealedIds = new Set<number>();
  videoFailed = false;
  /** False until the host taps play, which is what reveals the answer. */
  playStarted = false;
  petals: Petal[] = [];
  reducedMotion = false;

  @ViewChild('player') private playerRef?: ElementRef<HTMLVideoElement>;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.reducedMotion =
      typeof matchMedia === 'function' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get openCard(): ShowerQuestion | null {
    if (this.openId === this.bonus.id) {
      return this.bonus;
    }
    return this.questions.find(q => q.id === this.openId) ?? null;
  }

  /** True once all ten have been played. The bonus card does not count itself. */
  get allPlayed(): boolean {
    return this.questions.every(q => this.sealedIds.has(q.id));
  }

  cardNumber(id: number): string {
    return String(id).padStart(2, '0');
  }

  isSealed(id: number): boolean {
    return this.sealedIds.has(id);
  }

  open(q: ShowerQuestion): void {
    if (this.openId !== null) {
      return;
    }

    this.openId = q.id;
    this.videoFailed = false;
    this.playStarted = false;
    this.petals = this.reducedMotion ? [] : createPetals(14);

    // Deliberately no playback here. The card flips to the question and a play
    // button, giving the room a beat to guess before the answer is revealed.
  }

  close(): void {
    if (this.openId === null) {
      return;
    }

    const video = this.playerRef?.nativeElement;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    const closedId = this.openId;
    this.sealedIds.add(closedId);
    this.openId = null;
    this.videoFailed = false;
    this.playStarted = false;
    this.petals = [];

    this.cdr.detectChanges();

    const bonusEl = document.getElementById(`bsq-card-${this.bonus.id}`);
    if (closedId !== this.bonus.id && this.allPlayed && bonusEl) {
      // The bonus card has just been dealt in below ten sealed ones. Bring it
      // into view, or the surprise sits off the bottom of the screen.
      //
      // Deliberately an instant jump, not a smooth scroll: the card already has
      // its own entrance animation, and a smooth scroll can be interrupted or
      // throttled. This fires once, live, so it has to land every time.
      bonusEl.scrollIntoView({ behavior: 'auto', block: 'center' });
      bonusEl.focus({ preventScroll: true });
    } else {
      document.getElementById(`bsq-card-${closedId}`)?.focus();
    }
  }

  onVideoError(): void {
    this.videoFailed = true;
  }

  /**
   * Starts the answer. Called straight from the play button's tap, so playback
   * is always attributed to a real user gesture — no autoplay policy involved.
   */
  startVideo(): void {
    const video = this.playerRef?.nativeElement;
    if (!video) {
      return;
    }

    this.playStarted = true;
    video.muted = false;

    const started = video.play();
    if (started) {
      started.catch(() => {
        // Playback refused, or the file is missing. Put the button back so the
        // host can try again rather than being left with a dead panel.
        this.playStarted = false;
        this.cdr.detectChanges();
      });
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }
}
